import { useEffect, useMemo } from "react";

import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query/react";

import { resetAltQuotesReducer } from "domains/altQuotes/store/altQuotes.reducer";
import { useLiquiditySelector } from "domains/liquidity/store/liquidity.selectors";
import { AltQuote } from "domains/altQuotes/components";
import { Spinner } from "common/lib/components";
import { useInternalAltQuoteFunctionality } from "domains/altQuotes";
import { AssetType } from "domains/liquidity/shared/constants";
import { LiquidityRequestStatus } from "domains/liquidity/shared/types";
import { liquidityApiReducer } from "domains/liquidity/store/liquidity.api.reducer";
import { altQuotesApiReducer } from "domains/altQuotes/store/altQuotes.api.reducer";

const AltQuotePage = () => {
  const dispatch = useDispatch();
  const { LRId } = useParams();

  const { isInternalAltQuoteFunctionality } = useInternalAltQuoteFunctionality();
  const { currentLiquidityRequest } = useLiquiditySelector();
  
  // DEBUG LOGGING
  console.log('🔍 AltQuotePage - Route params:', { LRId });
  console.log('🔍 AltQuotePage - currentLiquidityRequest:', {
    id: currentLiquidityRequest?.id,
    display_id: currentLiquidityRequest?.display_id,
    status: currentLiquidityRequest?.status,
  });
  
  const selectAssetsResult = useMemo(
    () => liquidityApiReducer.endpoints.fetchLRAssets.select(LRId ?? skipToken),
    [ LRId ],
  );
  const { data: assets = [] } = useSelector(selectAssetsResult);
  console.log('🔍 AltQuotePage - Assets fetched:', { count: assets?.length, LRIdUsed: LRId });
  const [ updateLRCurrentTask ] = liquidityApiReducer.useUpdateLRCurrentTaskMutation();
  const [ updateLiquidityRequest ] = liquidityApiReducer.useUpdateLiquidityRequestMutation({
    fixedCacheKey: `update-liquidity-request-${ LRId }`,
  });
  const [
    fetchAltQuoteOffers,
    {
      data: altQuoteOffers = {
        alt_quote_id: null,
        fundOffers: [],
        offers: [],
      },
      isFetching: isFetchingAltQuoteOffers,
    },
  ] = altQuotesApiReducer.useLazyFetchAltQuoteOffersQuery();

  const currentTask = currentLiquidityRequest?.currentTask;
  const isFormalProposalRequested = currentLiquidityRequest?.is_formal_proposal_requested;
  const isAltQuoteClosed = currentTask ? currentTask.liquidity_request_status === LiquidityRequestStatus.Closed : true;
  const isSingleFundView = !(currentLiquidityRequest && assets?.length > 1);
  const isDataReceived = !!altQuoteOffers.alt_quote_id;
  const fundAssets = useMemo(
    () => assets?.filter((asset) => asset.asset_type === AssetType.Fund || asset.asset_type === AssetType.PreqinFund),
    [ assets?.length ],
  );
  const nonFundAssets = useMemo(
    () =>
      assets?.filter((asset) => !(asset.asset_type === AssetType.Fund || asset.asset_type === AssetType.PreqinFund)),
    [ assets?.length ],
  );
  const isFundAssetsSubmitted = !!fundAssets?.length;
  const isNonFundAssetsSubmitted = !!nonFundAssets?.length;
  const isOnlyNonFundAssetsSubmitted = !isFundAssetsSubmitted && isNonFundAssetsSubmitted;
  const isMixOfAssetsSubmitted = isFundAssetsSubmitted && isNonFundAssetsSubmitted;

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval>;
    if (!isDataReceived && LRId && !isOnlyNonFundAssetsSubmitted) {
      console.log('⏱️ Starting interval fetch for AltQuoteOffers with LRId:', LRId);
      intervalId = setInterval(() => {
        console.log('⏱️ Interval: Calling fetchAltQuoteOffers with LRId:', LRId);
        fetchAltQuoteOffers(LRId);
      }, 3000);
    }

    return () => clearInterval(intervalId);
  }, [ isDataReceived, LRId, isOnlyNonFundAssetsSubmitted ]);

  useEffect(() => {
    if (!LRId || isOnlyNonFundAssetsSubmitted) return;
    console.log('📤 useEffect: Calling fetchAltQuoteOffers with LRId:', LRId);
    fetchAltQuoteOffers(LRId);

    return () => {
      console.log('🔄 useEffect cleanup: Resetting AltQuotes reducer');
      dispatch(resetAltQuotesReducer());
    };
  }, [ LRId, isOnlyNonFundAssetsSubmitted ]);

  useEffect(() => {
    console.log('Effect running with:', { isDataReceived, LRId, isAltQuoteClosed });
    
    if (!isDataReceived || !LRId) {
      console.log('⏭️ Skipping view tracking effect:', { isDataReceived, LRId });
      return;
    }

    console.log('✅ Mark viewed + update current task with LRId:', LRId);
    // Mark that the client has viewed the altQuote offer
    updateLiquidityRequest({ 
      LRId, 
      changes: { is_alt_quote_offer_viewed: true }, 
    });

    updateLRCurrentTask(LRId);
  }, [ isDataReceived, LRId ]);

  useEffect(() => {
    if ((!fundAssets?.length && !nonFundAssets?.length) || !isOnlyNonFundAssetsSubmitted || !LRId || isAltQuoteClosed) {
      console.log('⏭️ Skipping close altquote effect:', { 
        fundAssetsLength: fundAssets?.length, 
        nonFundAssetsLength: nonFundAssets?.length,
        isOnlyNonFundAssetsSubmitted,
        LRId,
        isAltQuoteClosed, 
      });
      return;
    }

    console.log('🔒 Closing AltQuote with LRId:', LRId);
    updateLiquidityRequest({ LRId, changes: { status: LiquidityRequestStatus.Closed } });
  }, [ isOnlyNonFundAssetsSubmitted, LRId ]);

  console.log('🎬 AltQuotePage render:', {
    LRId,
    'currentLiquidityRequest.id': currentLiquidityRequest?.id,
    'currentLiquidityRequest.display_id': currentLiquidityRequest?.display_id,
    isDataReceived,
    isFetchingAltQuoteOffers,
    assetsCount: assets?.length,
    'altQuoteOffers.alt_quote_id': altQuoteOffers?.alt_quote_id,
  });

  return (
    <>
      <Spinner spinning={ isFetchingAltQuoteOffers }>
        <AltQuote
          altQuoteOffers={ altQuoteOffers }
          isSingleFundView={ isSingleFundView }
          LRId={ LRId ?? "" }
          liquidityRequest={ currentLiquidityRequest }
          isOnlyNonFundAssetsSubmitted={ isOnlyNonFundAssetsSubmitted }
          isMixOfAssetsSubmitted={ isMixOfAssetsSubmitted }
          isFormalProposalRequested={ isFormalProposalRequested }
        />
      </Spinner>

      {isInternalAltQuoteFunctionality &&
        !isOnlyNonFundAssetsSubmitted &&
        (!isDataReceived ? (
          <>
            <h3 className="ben-mt-4 ben-mr-2" style={ { display: "inline-block" } }>
              Waiting for AltQuote results
            </h3>
            <Spinner spinning={ true }></Spinner>
          </>
        ) : null)}
    </>
  );
};

export default AltQuotePage;
